/**
 * Atlas PM · 项目管理 · 新建 / 编辑表单页
 *
 * 字段较多（> 5），按交互规范使用**独立页**而非弹窗。
 * 字段严格对齐后端 CreateProjectDto；其中 longitude / latitude 后端必填，
 * 由「省 / 市 / 区县」三级联动**自动带出**（区县 > 市 > 省中心点），
 * 不再提供坐标输入框（P6.65）。
 *
 * 校验：必填项失焦即校验；后端错误 message 原样展示，不吞掉。
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/Button';
import FormField from '../../components/FormField';
import Panel from '../../components/Panel';
import Spinner from '../../components/Spinner';
import UserPicker from '../../components/UserPicker';
import { ApiError } from '../../api/client';
import {
  PROJECT_OPERATING_MODE,
  PROJECT_OPERATING_MODE_OPTIONS,
} from '@atlaspm/shared';
import {
  findDivision,
  resolveCenter,
  type Division,
} from '../../data/china-divisions';
import { projectTypeColor, type ProjectDetail, type ProjectInput } from './api';
import {
  useCreateProject,
  useDivisions,
  useProjectDetail,
  useUpdateProject,
} from './useProjects';
import { useProjectTypes } from './useProjectTypes';
import ProjectAutoTaskPicker from './ProjectAutoTaskPicker';
import ProjectAutoTaskPanel from './ProjectAutoTaskPanel';
import type { AutoTaskInput } from './autoTaskApi';
import './project.css';

type FieldKey =
  | 'code'
  | 'name'
  | 'operatingMode'
  | 'projectType'
  | 'provinceCode'
  | 'cityCode';

interface FormState {
  code: string;
  name: string;
  /** ⭐ P8.7 运营模式（POS / DOS，必填） */
  operatingMode: string;
  projectType: string;
  provinceCode: string;
  provinceName: string;
  cityCode: string;
  cityName: string;
  districtCode: string;
  /** 区县名仅用于回显，后端无此字段，不随 DTO 提交 */
  districtName: string;
  longitude: string;
  latitude: string;
  startDate: string;
  endDate: string;
  remark: string;
  ownerId: string;
  /** ⭐ P8.5 项目成员（可多个操作员） */
  memberIds: string[];
  /** ⭐ P8.29 建项目时选模板 + 当场设周期（仅 create 生效） */
  autoTasks: AutoTaskInput[];
}

const EMPTY: FormState = {
  code: '',
  name: '',
  operatingMode: PROJECT_OPERATING_MODE.POS,
  projectType: '',
  provinceCode: '',
  provinceName: '',
  cityCode: '',
  cityName: '',
  districtCode: '',
  districtName: '',
  longitude: '',
  latitude: '',
  startDate: '',
  endDate: '',
  remark: '',
  ownerId: '',
  memberIds: [],
  autoTasks: [],
};

function validateField(key: FieldKey, form: FormState): string | undefined {
  const v = form[key].trim();
  if (key === 'code') return v ? undefined : '项目编号不能为空';
  if (key === 'name') return v ? undefined : '项目名称不能为空';
  if (key === 'operatingMode') return v ? undefined : '请选择运营模式';
  if (key === 'projectType') return v ? undefined : '请选择项目类型';
  if (key === 'provinceCode') return v ? undefined : '请选择省份';
  if (key === 'cityCode') return v ? undefined : '请选择城市';
  return undefined;
}

const REQUIRED_KEYS: readonly FieldKey[] = [
  'code',
  'name',
  'operatingMode',
  'projectType',
  'provinceCode',
];

/** 按 adcode 排序（与地图省份口径一致） */
function byCode(a: Division, b: Division): number {
  return a.code.localeCompare(b.code);
}

export interface ProjectFormProps {
  /** create=新建；edit=编辑 */
  mode: 'create' | 'edit';
  projectId?: string;
  /**
   * 成功后回调（弹窗模式）。
   * 不传时回退为跳转 /map（页面模式已下线，默认留在态势图）。
   */
  onSaved?(project: ProjectDetail): void;
  /** 取消 / 关闭回调（弹窗模式）。不传时回退为跳转 /map。 */
  onCancel?(): void;
  /** 内嵌于弹窗：去掉页面外壳，只渲染表单本身 */
  embedded?: boolean;
}

export default function ProjectForm({
  mode,
  projectId,
  onSaved,
  onCancel,
  embedded = false,
}: ProjectFormProps) {
  const navigate = useNavigate();
  const isEdit = mode === 'edit';

  const handleCancel = () => {
    if (onCancel) onCancel();
    else navigate('/map');
  };

  const divisionsQ = useDivisions();
  const projectTypesQ = useProjectTypes();
  const detailQ = useProjectDetail(isEdit ? projectId : undefined);
  const createMut = useCreateProject();
  const updateMut = useUpdateProject(projectId ?? '');

  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<FieldKey, boolean>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const mutation = isEdit ? updateMut : createMut;
  const busy = mutation.isPending;

  // 编辑：详情加载完成后回填表单
  useEffect(() => {
    const p = detailQ.data;
    if (!isEdit || !p) return;
    setForm({
      code: p.code,
      name: p.name,
      operatingMode: p.operatingMode || PROJECT_OPERATING_MODE.POS,
      projectType: p.projectType,
      provinceCode: p.provinceCode,
      provinceName: p.provinceName,
      cityCode: p.cityCode ?? '',
      cityName: p.cityName ?? '',
      districtCode: p.districtCode ?? '',
      districtName: '',
      // ⚠️ 沿用数据库已有坐标，不因回填而覆盖
      longitude: String(p.longitude),
      latitude: String(p.latitude),
      startDate: p.startDate ?? '',
      endDate: p.endDate ?? '',
      remark: p.remark ?? '',
      ownerId: p.ownerId ?? '',
      memberIds: p.memberIds ?? [],
      // ⭐ P8.29 编辑态不使用 picker（自动任务走项目详情区块 4.2）
      autoTasks: [],
    });
  }, [isEdit, detailQ.data]);

  // ⭐ P6.65 三级行政区划（动态 import 分包）
  const divisions = divisionsQ.data ?? [];
  const provinceOptions = useMemo(
    () => divisions.filter((d) => d.level === 'province').sort(byCode),
    [divisions],
  );
  const cityOptions = useMemo(
    () => divisions.filter((d) => d.level === 'city' && d.parent === form.provinceCode).sort(byCode),
    [divisions, form.provinceCode],
  );
  const districtOptions = useMemo(() => {
    // 区县的上级：有市时是市；直辖市 / 港澳台的区县直属省级
    const parent = form.cityCode || form.provinceCode;
    return divisions.filter((d) => d.level === 'district' && d.parent === parent).sort(byCode);
  }, [divisions, form.cityCode, form.provinceCode]);
  // 该省是否有地级市（直辖市 / 港澳台无此层，则市不参与必填）
  const provinceHasCities = useMemo(
    () => divisions.some((d) => d.level === 'city' && d.parent === form.provinceCode),
    [divisions, form.provinceCode],
  );
  // ⭐ 市必填仅限【新建】；编辑历史项目（如 608/625 只有省份）不强制补市，
  //    以保证既有项目可直接保存且坐标不被改动（验收 #8/#9）。
  const cityRequired = provinceHasCities && !isEdit;

  const coordText = useMemo(() => {
    const lng = Number(form.longitude);
    const lat = Number(form.latitude);
    if (!form.longitude || !form.latitude || Number.isNaN(lng) || Number.isNaN(lat)) return null;
    return `${lng.toFixed(4)}, ${lat.toFixed(4)}`;
  }, [form.longitude, form.latitude]);

  const cityHint = !form.provinceCode
    ? '请先选择省份'
    : provinceHasCities
      ? `地级市 / 自治州 / 盟（${cityRequired ? '必填' : '选填'}）`
      : '该省为直辖市 / 特别行政区，无市级，请直接选「区县」';
  const districtHint = !form.provinceCode
    ? '请先选择省份'
    : provinceHasCities && !form.cityCode
      ? '请先选择城市'
      : districtOptions.length > 0
        ? '选填；不选则定位到市中心点'
        : '该市无下辖区县，将定位到市中心点';

  // 当前选中类型的业务色（API 数据优先，回退运行时缓存）
  const selectedTypeColor = useMemo(
    () =>
      projectTypesQ.options.find((t) => t.value === form.projectType)?.color ??
      projectTypeColor(form.projectType),
    [projectTypesQ.options, form.projectType],
  );

  const setField = (key: keyof FormState, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (key in errors) {
      setErrors((e) => ({ ...e, [key]: undefined }));
    }
    setSubmitError(null);
  };

  const onBlurField = (key: FieldKey) => {
    setTouched((t) => ({ ...t, [key]: true }));
    setErrors((e) => ({ ...e, [key]: validateField(key, form) }));
  };

  /**
   * ⭐ P8.5：负责人变更时，若此人已在成员列表里则自动移除，
   * 保证「一个人不会同时是负责人和成员」。
   */
  const setOwner = (id: string) => {
    setForm((f) => ({
      ...f,
      ownerId: id,
      memberIds: id ? f.memberIds.filter((m) => m !== id) : f.memberIds,
    }));
    setSubmitError(null);
  };

  const setMembers = (ids: string[]) => {
    setForm((f) => ({ ...f, memberIds: ids }));
    setSubmitError(null);
  };

  /** ⭐ P8.29 建项目：选模板 + 当场设周期 */
  const setAutoTasks = (items: AutoTaskInput[]) => {
    setForm((f) => ({ ...f, autoTasks: items }));
    setSubmitError(null);
  };

  /** 选定行政区后把中心点写入坐标（优先级：区县 > 市 > 省） */
  const applyCenter = (
    next: FormState,
    picks: { province?: Division; city?: Division; district?: Division },
  ): FormState => {
    const center = resolveCenter(picks.province, picks.city, picks.district);
    return center ? { ...next, longitude: String(center[0]), latitude: String(center[1]) } : next;
  };

  const onProvinceChange = (code: string) => {
    const prov = findDivision(divisions, code);
    setForm((f) => {
      // 上级变更 → 清空下级，避免「浙江省 + 成都市」这类矛盾组合
      const next: FormState = {
        ...f,
        provinceCode: code,
        provinceName: prov?.name ?? '',
        cityCode: '',
        cityName: '',
        districtCode: '',
        districtName: '',
      };
      return applyCenter(next, { province: prov });
    });
    setErrors((e) => ({ ...e, provinceCode: undefined, cityCode: undefined }));
    setSubmitError(null);
  };

  const onCityChange = (code: string) => {
    const prov = findDivision(divisions, form.provinceCode);
    const city = findDivision(divisions, code);
    setForm((f) => {
      // 市变更 → 清空区县
      const next: FormState = {
        ...f,
        cityCode: code,
        cityName: city?.name ?? '',
        districtCode: '',
        districtName: '',
      };
      return applyCenter(next, { province: prov, city });
    });
    setErrors((e) => ({ ...e, cityCode: undefined }));
    setSubmitError(null);
  };

  const onDistrictChange = (code: string) => {
    const prov = findDivision(divisions, form.provinceCode);
    const city = findDivision(divisions, form.cityCode);
    const district = findDivision(divisions, code);
    setForm((f) => {
      const next: FormState = {
        ...f,
        districtCode: code,
        districtName: district?.name ?? '',
      };
      return applyCenter(next, { province: prov, city, district });
    });
    setSubmitError(null);
  };

  const autoCode = () => {
    const year = new Date().getFullYear();
    const seq = String(Math.floor(Math.random() * 9000) + 1000);
    setField('code', `PRJ-${year}-${seq}`);
  };

  const buildInput = (): ProjectInput => ({
    code: form.code.trim(),
    name: form.name.trim(),
    operatingMode: (form.operatingMode || PROJECT_OPERATING_MODE.POS) as 'POS' | 'DOS',
    projectType: form.projectType,
    provinceCode: form.provinceCode,
    provinceName: form.provinceName || findDivision(divisions, form.provinceCode)?.name || undefined,
    cityCode: form.cityCode || undefined,
    cityName: form.cityName || findDivision(divisions, form.cityCode)?.name || undefined,
    districtCode: form.districtCode || undefined,
    longitude: Number(form.longitude),
    latitude: Number(form.latitude),
    startDate: form.startDate || undefined,
    endDate: form.endDate || undefined,
    remark: form.remark.trim() || undefined,
    // ⭐ P6.27：显式传空串以支持「清空负责人」（后端 bigintOrNull('') → null）
    ownerId: form.ownerId,
    // ⭐ P8.5：始终传数组（空数组 = 清空全部成员；负责人不重复计入成员）
    memberIds: form.memberIds.filter((id) => id !== form.ownerId),
    // ⭐ P8.29：仅新建携带自动任务配置（autoTasks）；编辑走项目详情的自动任务区块
    ...(isEdit ? {} : { autoTasks: form.autoTasks }),
  });

  const onSubmit = () => {
    const nextErrors: Partial<Record<FieldKey, string>> = {};
    for (const k of REQUIRED_KEYS) {
      const msg = validateField(k, form);
      if (msg) nextErrors[k] = msg;
    }
    // 市：该省有地级市时必填；直辖市 / 港澳台无市级，跳过
    if (!nextErrors.provinceCode && cityRequired && !form.cityCode.trim()) {
      nextErrors.cityCode = '请选择城市';
    }
    setErrors(nextErrors);
    const touchedKeys: FieldKey[] = cityRequired ? [...REQUIRED_KEYS, 'cityCode'] : [...REQUIRED_KEYS];
    setTouched(Object.fromEntries(touchedKeys.map((k) => [k, true])));
    if (Object.keys(nextErrors).length > 0) return;

    setSubmitError(null);
    // ⚠️ 已无坐标输入框：坐标必须由所选行政区自动带出
    if (!coordText) {
      setSubmitError('未能自动带出坐标，请重新选择省 / 市 / 区县');
      return;
    }
    const input = buildInput();
    const onSuccess = (saved: ProjectDetail) => {
      if (onSaved) onSaved(saved);
      else navigate('/map');
    };
    if (isEdit) {
      updateMut.mutate(input, {
        onSuccess,
        onError: (err) => setSubmitError(err instanceof Error ? err.message : '保存失败'),
      });
    } else {
      createMut.mutate(input, {
        onSuccess,
        onError: (err) =>
          setSubmitError(
            err instanceof ApiError ? err.message : err instanceof Error ? err.message : '创建失败',
          ),
      });
    }
  };

  const showErr = (key: FieldKey) => (touched[key] ? errors[key] : undefined);

  // 编辑态加载中 / 加载失败
  if (isEdit && detailQ.isLoading) {
    if (embedded) return <Spinner label="加载项目…" />;
    return (
      <div className="page pm-page">
        <Spinner label="加载项目…" />
      </div>
    );
  }
  if (isEdit && (detailQ.isError || !detailQ.data)) {
    const failMsg =
      detailQ.error instanceof Error ? detailQ.error.message : '项目不存在或无权访问';
    if (embedded) {
      return (
        <div className="pm-form--dialog">
          <div className="pm-form__error">⚠ {failMsg}</div>
          <div className="pm-form__foot">
            <Button variant="secondary" onClick={handleCancel}>
              关闭
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className="page pm-page">
        <Panel title="项目加载失败">
          <div className="pm-form__error">{failMsg}</div>
          <Button variant="secondary" onClick={handleCancel}>
            返回
          </Button>
        </Panel>
      </div>
    );
  }

  const formEl = (
    <form
      data-testid="project-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
          {submitError && <div className="pm-form__error">⚠ {submitError}</div>}

          <div className="pm-form__grid">
            <FormField
              label="项目编号"
              required
              htmlFor="pf-code"
              error={showErr('code')}
              hint="全局唯一，格式示例 PRJ-2026-0001"
            >
              {isEdit ? (
                <input id="pf-code" className="pm-input" value={form.code} disabled />
              ) : (
                <div className="pm-input-row">
                  <input
                    id="pf-code"
                    className="pm-input"
                    data-testid="field-code"
                    placeholder="PRJ-2026-0001"
                    value={form.code}
                    onChange={(e) => setField('code', e.target.value)}
                    onBlur={() => onBlurField('code')}
                  />
                  <Button type="button" variant="secondary" onClick={autoCode}>
                    自动生成
                  </Button>
                </div>
              )}
            </FormField>

            <FormField label="项目名称" required htmlFor="pf-name" error={showErr('name')}>
              <input
                id="pf-name"
                className="pm-input"
                data-testid="field-name"
                placeholder="如：杭州市智慧园区运营项目"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                onBlur={() => onBlurField('name')}
              />
            </FormField>

            <FormField
              label="运营模式"
              required
              htmlFor="pf-operating-mode"
              error={showErr('operatingMode')}
              hint="POS（全运营服务）/ DOS（数据运营服务）"
            >
              <select
                id="pf-operating-mode"
                className="pm-select"
                data-testid="field-operating-mode"
                value={form.operatingMode}
                onChange={(e) => setField('operatingMode', e.target.value)}
                onBlur={() => onBlurField('operatingMode')}
              >
                {PROJECT_OPERATING_MODE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField
              label="项目类型"
              required
              htmlFor="pf-type"
              error={showErr('projectType')}
              hint={projectTypesQ.isError ? '类型接口暂不可用，已回退内置列表' : undefined}
            >
              <div className="pm-input-row">
                <select
                  id="pf-type"
                  className="pm-select"
                  data-testid="field-projectType"
                  value={form.projectType}
                  onChange={(e) => setField('projectType', e.target.value)}
                  onBlur={() => onBlurField('projectType')}
                >
                  <option value="">请选择项目类型</option>
                  {projectTypesQ.options.map((t) => (
                    <option
                      key={t.value}
                      value={t.value}
                      data-color={t.color ?? ''}
                      style={t.color ? { color: t.color } : undefined}
                    >
                      {t.label}
                    </option>
                  ))}
                </select>
                {selectedTypeColor && (
                  <span
                    className="pm-type-swatch"
                    data-testid="projectType-swatch"
                    style={{ background: selectedTypeColor }}
                    title="项目类型标识色"
                    aria-hidden="true"
                  />
                )}
              </div>
            </FormField>

            <FormField
              label="项目负责人"
              htmlFor="pf-owner"
              hint={
                form.ownerId
                  ? '项目第一责任人（候选仅操作员）；任务由所属省管理员审批'
                  : isEdit
                    ? '⚠ 尚未设置负责人（候选仅操作员）；任务审批由所属省管理员负责'
                    : '⚠ 建议指定一名操作员作为负责人（候选仅操作员）'
              }
            >
              <UserPicker
                value={form.ownerId}
                onChange={(id) => setOwner(id)}
                dataTestId="field-owner"
                placeholder="搜索操作员姓名 / 用户名"
                allowClear
              />
            </FormField>

            {/* ⭐ P8.5：项目成员（多选）——成员可查看项目、创建 / 编辑任务，但不能改项目本身 */}
            <FormField
              label="项目成员"
              htmlFor="pf-members"
              hint="成员可查看本项目、创建与编辑任务；项目信息本身仅负责人/管理员可改"
            >
              <UserPicker
                multiple
                values={form.memberIds}
                onChangeMulti={(ids) => setMembers(ids)}
                excludeIds={form.ownerId ? [form.ownerId] : []}
                dataTestId="field-members"
                placeholder="搜索操作员姓名 / 用户名"
                allowClear
              />
            </FormField>

            <div className="pm-region-grid pm-field--full">
            <FormField
              label="省份"
              required
              htmlFor="pf-province"
              error={showErr('provinceCode')}
              hint={form.provinceName ? `已选：${form.provinceName}` : '省级行政区（含港澳台），共 34 个'}
            >
              <select
                id="pf-province"
                className="pm-select"
                data-testid="field-provinceCode"
                value={form.provinceCode}
                onChange={(e) => onProvinceChange(e.target.value)}
                onBlur={() => onBlurField('provinceCode')}
              >
                <option value="">请选择省份</option>
                {provinceOptions.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.name}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField
              label="城市"
              required={cityRequired}
              htmlFor="pf-city"
              error={showErr('cityCode')}
              hint={cityHint}
            >
              <select
                id="pf-city"
                className="pm-select"
                data-testid="field-cityCode"
                value={form.cityCode}
                disabled={!form.provinceCode || !provinceHasCities}
                onChange={(e) => onCityChange(e.target.value)}
                onBlur={() => onBlurField('cityCode')}
              >
                <option value="">
                  {!form.provinceCode
                    ? '请先选择省份'
                    : provinceHasCities
                      ? '请选择城市'
                      : '（该省无市级）'}
                </option>
                {cityOptions.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="区县" htmlFor="pf-district" hint={districtHint}>
              <select
                id="pf-district"
                className="pm-select"
                data-testid="field-districtCode"
                value={form.districtCode}
                disabled={districtOptions.length === 0}
                onChange={(e) => onDistrictChange(e.target.value)}
              >
                <option value="">
                  {districtOptions.length > 0 ? '请选择区县（选填）' : '（无下辖区县）'}
                </option>
                {districtOptions.map((d) => (
                  <option key={d.code} value={d.code}>
                    {d.name}
                  </option>
                ))}
              </select>
            </FormField>
            </div>

            <FormField label="开始日期" htmlFor="pf-start">
              <input
                id="pf-start"
                type="date"
                className="pm-input"
                value={form.startDate}
                onChange={(e) => setField('startDate', e.target.value)}
              />
            </FormField>

            <FormField label="结束日期" htmlFor="pf-end">
              <input
                id="pf-end"
                type="date"
                className="pm-input"
                value={form.endDate}
                onChange={(e) => setField('endDate', e.target.value)}
              />
            </FormField>

            <FormField label="备注" htmlFor="pf-remark" className="pm-field--full">
              <textarea
                id="pf-remark"
                className="pm-textarea"
                placeholder="选填"
                value={form.remark}
                onChange={(e) => setField('remark', e.target.value)}
              />
            </FormField>

            {/* ⭐ P8.29：自动创建任务 —— 新建 picker（模板 + 周期）/ 编辑区块 */}
            {!isEdit && (
              <FormField
                label="自动创建任务"
                className="pm-field--full"
                hint="可选，可加多条；选择任务模板并设置周期后，创建项目时立即生成当前周期的对应任务"
              >
                <ProjectAutoTaskPicker value={form.autoTasks} onChange={setAutoTasks} />
              </FormField>
            )}
            {isEdit && (
              <div className="pm-field--full">
                <ProjectAutoTaskPanel projectId={projectId ?? ''} />
              </div>
            )}
          </div>

          <div className="pm-form__foot">
            <Button type="button" variant="ghost" disabled={busy} onClick={handleCancel}>
              取消
            </Button>
            <Button type="submit" variant="primary" data-testid="project-submit" disabled={busy}>
              {busy ? '提交中…' : isEdit ? '保存修改' : '创建项目'}
            </Button>
          </div>
        </form>
  );

  if (embedded) {
    return <div className="pm-form pm-form--dialog">{formEl}</div>;
  }

  return (
    <div className="page pm-page">
      <div className="pm-page__head">
        <div className="page__head" style={{ marginBottom: 0 }}>
          <h1 className="page__title">{isEdit ? '编辑项目' : '新建项目'}</h1>
          <div className="page__sub">
            {isEdit ? '编号创建后不可修改' : '创建后自动进入「筹备中」，可在任务中推进'}
          </div>
        </div>
      </div>
      <Panel className="pm-form">{formEl}</Panel>
    </div>
  );
}
